import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { finalize } from "rxjs";
import {
  ProjectService,
  ProjectTableDTO,
} from "src/shared/api/generated";
import { CompetencyManagerService } from "src/shared/services/competency-manager.service";
import { ConfirmationModalService } from "src/shared/services/confirmation-modal.service";
import { isDeepEqual } from "src/shared/util/is-deep-equal";

import { ProjectMembersModalComponent } from "../../project-members-modal/project-members-modal.component";

@Component({
  selector: "[app-tr-project]",
  templateUrl: "./tr-project.component.html",
  styleUrls: ["./tr-project.component.scss"],
})
export class TrProjectComponent implements OnInit {
  @Input() project: ProjectTableDTO = {} as ProjectTableDTO;
  @Input() searchTerm: string | undefined = "";
  @Input() search: boolean = false;
  @Input() allCompetencies: Map<number, string> = new Map<number, string>();
  @Input() createMode: boolean = false;
  @Input() cloneMode: boolean = false;

  @Output() onMembersSaved = new EventEmitter<void>();

  editProject: ProjectTableDTO = {} as ProjectTableDTO;
  selectedMemberCellProjectId: number | null = null;

  editedCell: string = "";

  constructor(
    private competencyManagerService: CompetencyManagerService,
    private modalService: NgbModal,
    private projectService: ProjectService,
    private confirmationModalService: ConfirmationModalService
  ) {}

  ngOnInit(): void {
    this.resetEditedProject();
    if (this.createMode || this.cloneMode) {
      this.competencyManagerService.clearProjectCompetency();
    }
  }

  handleProjectUpdate(): void {
    if (this.createMode || this.cloneMode) return;

    if (
      isDeepEqual(this.editProject, this.project) ||
      !this.editProject.name.trim()
    ) {
      this.setEditStateFalse();
      return;
    }

    this.updateProject({
      ...this.editProject,
      name: this.editProject.name.trim(),
      alternativeName: this.editProject.alternativeName?.trim(),
      description: this.editProject.description?.trim(),
    });
    this.setEditStateFalse();
  }

  private updateProject(project: ProjectTableDTO): void {
    this.projectService
      .updateProject(project)
      .pipe(finalize(() => this.resetEditedProject()))
      .subscribe({
        next: (savedProject) => (this.project = savedProject),
        error: (error) => this.handleProjectSaveError(error),
      });
  }

  private handleProjectSaveError(error: HttpErrorResponse): void {
    if (error.status === HttpStatusCode.Conflict) {
      this.confirmationModalService.openConfirmationModal(
        "project.error.title",
        "project.error.duplicate_name",
        true
      );
    } else if(error.status === HttpStatusCode.BadRequest) {
      this.confirmationModalService.openConfirmationModal(
        "validation_message.error.title",
        error.error.message,
        true
      );
    }
  }

  selectMemberCell($event: MouseEvent): void {
    this.inputClicked($event);
    if (!this.project.id || this.createMode || this.cloneMode) {
      return;
    }
    const modalRef = this.modalService.open(ProjectMembersModalComponent, {
      windowClass: "custom-modal-window",
      scrollable: true,
      keyboard: false,
    });

    modalRef.componentInstance.projectId = this.project.id;
    modalRef.componentInstance.projectName = this.project.name;
    modalRef.componentInstance.projectMembers = this.project.members;
    modalRef.componentInstance.allCompetencies = this.allCompetencies;
    this.selectedMemberCellProjectId = this.project.id;
    modalRef.closed.subscribe((result) => {
      if (result) this.onMembersSaved.emit();
      this.selectedMemberCellProjectId = null;
    });
    modalRef.dismissed.subscribe((result) => {
      if (result) this.onMembersSaved.emit();
      this.selectedMemberCellProjectId = null;
    });
  }

  setEditStateFalse(): void {
    if (this.createMode || this.cloneMode) return;

    this.resetEditedProject();
    this.editedCell = "";
  }

  setEditStateTrue(key: string) {
    if (this.createMode || this.cloneMode) {
      return;
    }
    this.editedCell = key;
  }

  resetEditedProject(): void {
    this.editProject = JSON.parse(JSON.stringify(this.project));
  }

  isEditingDisabled(): boolean {
    if (this.project.id && (this.createMode || this.cloneMode)) {
      return true;
    }
    return false;
  }

  isProjectStateSelectDisabled(): boolean {
    return (
      this.editProject.projectState ===
        ProjectTableDTO.ProjectStateEnum.Prospect &&
      (!this.editProject.start || !this.editProject.end)
    );
  }

  isCompetencySearchResult() {
    if (!this.search || !this.searchTerm) {
      return false;
    }
    return this.project.competencies?.filter((competency) => {
      return !!this.allCompetencies
        .get(competency.competencyId)
        ?.toLowerCase()
        .includes(this.searchTerm?.toLowerCase() ?? "");
    }).length;
  }

  isMemberSearchResult() {
    if (!this.search || !this.searchTerm) {
      return false;
    }
    return this.project.members?.filter((member) =>
      member.employeeName
        ?.toLowerCase()
        .includes(this.searchTerm!.toLowerCase())
    ).length;
  }

  inputClicked($event: MouseEvent) {
    $event.stopPropagation();
  }

}
